import { useAuth } from '../lib/auth';
import { t } from '../lib/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  api,
  type Decision,
  type HouseholdMember,
  type NoteStub,
  type Project,
  type Task,
  type WaitingItem,
} from '../lib/api';
import { buildQuickCapture, type QuickCaptureKind } from '../lib/quick-capture';
import { onEnter } from '../lib/keys';
import { TaskDetail } from './TaskDetail';
import { INBOX_ID, projectTitle } from '../lib/tasks';
import { workspaceComposerHref } from '../lib/workspace';

const CAPTURE_KINDS: QuickCaptureKind[] = ['task', 'waiting', 'decision', 'note'];

/**
 * Одна точка для несортированных мыслей. Важно, что это не AI-классификатор:
 * человек явно выбирает смысл записи. Иначе «жду ответ» незаметно станет
 * задачей и снова останется в голове вместо списка ожиданий.
 */
function kindLabel(kind: QuickCaptureKind): string {
  switch (kind) {
    case 'task':
      return t('Задача');
    case 'waiting':
      return t('Ожидание');
    case 'decision':
      return t('Решение');
    case 'note':
      return t('Заметка');
  }
}

function kindDescription(kind: QuickCaptureKind): string {
  switch (kind) {
    case 'task':
      return t('Нужно сделать');
    case 'waiting':
      return t('Жду ответ или действие');
    case 'decision':
      return t('Фиксирую правило');
    case 'note':
      return t('Сохраняю контекст');
  }
}

function titlePlaceholder(kind: QuickCaptureKind): string {
  switch (kind) {
    case 'task':
      return t('Что нужно сделать');
    case 'waiting':
      return t('Что ждём');
    case 'decision':
      return t('Что решили');
    case 'note':
      return t('Название заметки');
  }
}

function saveLabel(kind: QuickCaptureKind): string {
  switch (kind) {
    case 'task':
      return t('Добавить задачу');
    case 'waiting':
      return t('Добавить ожидание');
    case 'decision':
      return t('Добавить решение');
    case 'note':
      return t('Создать заметку');
  }
}

export function QuickAdd({ onAdded }: { onAdded: () => void }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<QuickCaptureKind>('task');
  const [projects, setProjects] = useState<Project[]>([]);
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState(INBOX_ID);
  const [dueDate, setDueDate] = useState('');
  const [waitingFor, setWaitingFor] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  // Созданная задача сразу открывается на редактирование:
  // срок, исполнителя и приоритет удобнее проставить по горячим следам.
  const [created, setCreated] = useState<Task | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const openCapture = useCallback(() => {
    if (user?.role === 'admin') {
      navigate(workspaceComposerHref(pathname, search));
      return;
    }
    setError(null);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    setOpen(true);
  }, [navigate, user?.role, pathname, search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openCapture();
      }
      if (e.key === 'Escape') closeCapture();
    };
    // Открытие извне — с экрана быстрых действий на телефоне.
    const openIt = () => openCapture();
    window.addEventListener('keydown', onKey);
    window.addEventListener('hub:quick-add', openIt);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hub:quick-add', openIt);
    };
  }, [openCapture]);

  function closeCapture() {
    setOpen(false);
    previouslyFocused.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    // Focus trap: Tab и Shift+Tab остаются внутри модального диалога.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Эти данные нужны только для ветки «задача». Загружаем их при открытии,
    // чтобы не добавлять отдельный запрос или схему для Quick Capture.
    void api.get<Project[]>('/projects').then(setProjects);
    void api.get<HouseholdMember[]>('/household').then(setMembers);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  function clearSavedDraft() {
    setTitle('');
    setDueDate('');
    setWaitingFor('');
    setFollowUpDate('');
    setError(null);
  }

  async function submit() {
    const capture = buildQuickCapture(kind, {
      title,
      projectId,
      dueDate,
      waitingFor,
      followUpDate,
    });
    if (!capture) {
      setError(
        kind === 'waiting' && !waitingFor.trim()
          ? t('Укажите, от кого ждёте ответ.')
          : t('Сначала сформулируйте мысль.'),
      );
      return;
    }

    setBusy(true);
    setError(null);
    try {
      switch (capture.kind) {
        case 'task': {
          const task = await api.post<Task>('/tasks', capture.body);
          clearSavedDraft();
          closeCapture();
          onAdded();
          setCreated(task);
          return;
        }
        case 'waiting': {
          await api.post<WaitingItem>('/waiting', capture.body);
          clearSavedDraft();
          closeCapture();
          onAdded();
          navigate('/waiting');
          return;
        }
        case 'decision': {
          await api.post<Decision>('/decisions', capture.body);
          clearSavedDraft();
          closeCapture();
          onAdded();
          navigate('/decisions');
          return;
        }
        case 'note': {
          const note = await api.post<NoteStub>('/notes', capture.body);
          clearSavedDraft();
          closeCapture();
          onAdded();
          navigate(`/notes?open=${note.id}`);
          return;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Не удалось добавить'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {!open && !['/', '/tasks', '/notes', '/calendar', '/business', '/leads', '/inbox', '/messages', '/objects', '/workspace', '/bookings', '/money', '/cleaning'].includes(pathname) && !pathname.startsWith('/runs') && !pathname.startsWith('/leads/') && !pathname.startsWith('/bookings/') && <button
        type="button"
        onClick={openCapture}
        className="jack-fab fixed right-5 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-20 grid size-13 place-items-center rounded-full bg-accent text-2xl text-white shadow-lg md:hidden"
        aria-label={t('Быстрый ввод')}
      >
        +
      </button>}

      {open && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto px-3 py-5 sm:px-5 sm:pt-24">
          <button
            type="button"
            aria-label={t('Закрыть')}
            onClick={() => closeCapture()}
            tabIndex={-1}
            className="absolute inset-0 bg-black/40"
          />
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('Быстрый ввод')}
            aria-busy={busy}
            className="relative w-full max-w-lg rounded-card border border-line bg-surface p-5 shadow-xl"
          >
            <h2 className="eyebrow">{t('Быстрый ввод')}</h2>
            <p className="mt-1 text-sm text-muted">{t('Сначала выберите, куда сохранить мысль.')}</p>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label={t('Сохранить как')}>
              {CAPTURE_KINDS.map((item) => {
                const selected = item === kind;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setKind(item);
                      setError(null);
                    }}
                    aria-pressed={selected}
                    className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                      selected
                        ? 'border-accent bg-accent/10 text-ink'
                        : 'border-line bg-surface-2 text-muted hover:border-accent/50'
                    }`}
                  >
                    <span className="block text-sm font-medium">{kindLabel(item)}</span>
                    <span className="mt-0.5 block text-xs leading-snug">{kindDescription(item)}</span>
                  </button>
                );
              })}
            </div>

            <input
              ref={inputRef}
              value={title}
              maxLength={200}
              placeholder={titlePlaceholder(kind)}
              aria-label={titlePlaceholder(kind)}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={onEnter(() => void submit())}
              className="mt-4 w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-ink outline-none focus:border-accent"
            />

            {kind === 'task' && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <select
                  value={projectId}
                  aria-label={t('Проект')}
                  onChange={(e) => setProjectId(e.target.value)}
                  onKeyDown={onEnter(() => void submit())}
                  className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {projectTitle(p.id, p.title)}
                    </option>
                  ))}
                </select>

                <input
                  type="date"
                  value={dueDate}
                  aria-label={t('Срок')}
                  onChange={(e) => setDueDate(e.target.value)}
                  onKeyDown={onEnter(() => void submit())}
                  className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                />
              </div>
            )}

            {kind === 'waiting' && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-muted">{t('От кого ждём')}</span>
                  <input
                    value={waitingFor}
                    maxLength={160}
                    placeholder={t('Имя человека или компании')}
                    onChange={(e) => setWaitingFor(e.target.value)}
                    onKeyDown={onEnter(() => void submit())}
                    className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs text-muted">{t('Следующее напоминание')}</span>
                  <input
                    type="date"
                    value={followUpDate}
                    aria-label={t('Следующее напоминание')}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    onKeyDown={onEnter(() => void submit())}
                    className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                  />
                </label>
              </div>
            )}

            {error && (
              <p role="alert" className="mt-3 text-sm text-urgent">
                {error}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between">
              <span className="hidden font-mono text-xs text-muted md:inline">Cmd + K</span>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {saveLabel(kind)}
              </button>
            </div>
          </div>
        </div>
      )}

      {created && (
        <TaskDetail
          task={created}
          members={members}
          projects={projects}
          onSaved={onAdded}
          onClose={() => setCreated(null)}
        />
      )}
    </>
  );
}
